/**
 * @fileoverview Shared edit-confirmation dialog for data agent detail panels.
 *
 * Purpose:
 * - Every inline edit in a data agent detail panel (Assets, Loans, Creditors,
 *   Security Interests, ATO/SRO, Employees, transactions) is confirmed before
 *   it is written, so a practitioner can't change agent-extracted data with a
 *   stray click. The dialog also captures an optional note explaining the
 *   change, which is written into the record's change history as the entry's
 *   reason line.
 *
 * Usage (per panel):
 * - Hold `const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null)`.
 * - Instead of firing the mutation directly, set a `PendingEdit` whose
 *   `onConfirm` fires the mutation with the practitioner's note.
 * - Render `<ConfirmEditDialog edit={pendingEdit} onClose={() => setPendingEdit(null)} />`.
 *
 * Used by:
 * - AssetPanel, LoanDetailPanel, LiabilityPanel, SecurityInterestDetailPanel,
 *   AtoSroPanel, EmployeeDetailPanel, TransactionDetailPanel (asset sales),
 *   TransactionDetailSheet (bank transactions).
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/ui/composed/overlays/dialog";
import { Button } from "@/ui/primitives/button";
import { Textarea } from "@/ui/primitives/textarea";

/**
 * One edit awaiting confirmation. Created by a panel's save handler in place
 * of firing the mutation directly.
 */
export interface PendingEdit {
  /** Human label of what's changing, e.g. "Location" or "Valuation amount". */
  fieldLabel: string;
  /**
   * Fires the underlying mutation. `note` is the practitioner's optional
   * explanation (undefined when left blank) — recorded in change history.
   *
   * Return the mutation promise (React Query `mutateAsync`) so the dialog can
   * stay open until the write succeeds: a rejection keeps the staged edit and
   * surfaces a toast instead of silently discarding the change.
   */
  onConfirm: (note: string | undefined) => Promise<unknown>;
}

/**
 * Confirmation dialog for a pending panel edit.
 *
 * Open whenever `edit` is non-null. Confirming awaits `edit.onConfirm(note)`
 * and closes only on success — a rejected mutation keeps the dialog (and the
 * staged edit) open and shows an error toast. Cancelling just closes — no
 * mutation fires, so the record is untouched.
 *
 * @component
 * @example
 * <ConfirmEditDialog edit={pendingEdit} onClose={() => setPendingEdit(null)} />
 */
export function ConfirmEditDialog({
  edit,
  onClose,
}: {
  edit: PendingEdit | null;
  onClose: () => void;
}) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const open = edit !== null;

  // Reset the note whenever a new edit is staged so a prior note never leaks
  // into an unrelated change.
  useEffect(() => {
    if (open) {
      setNote("");
      setSaving(false);
    }
  }, [open, edit]);

  const handleConfirm = async () => {
    if (!edit) return;
    try {
      setSaving(true);
      await edit.onConfirm(note.trim() || undefined);
      onClose();
    } catch {
      toast.error(
        `Couldn't save the change to ${edit.fieldLabel} — please try again.`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent
        className="sm:max-w-md"
        // The detail panels behind this dialog close themselves on a
        // window-level Escape keydown (SlideOverPanel and the inline handlers
        // in the transaction/employee panels). Radix handles Escape in a
        // capture-phase document listener, so stopping propagation here lets
        // Escape dismiss just this dialog without also closing the panel and
        // losing the panel context.
        onEscapeKeyDown={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Confirm change</DialogTitle>
          <DialogDescription>
            You&rsquo;re about to change{" "}
            <span className="font-medium text-foreground">
              {edit?.fieldLabel}
            </span>{" "}
            on this record. The change is saved to the record&rsquo;s history
            and marks it as manually edited.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Note (optional)
          </label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why are you making this change?"
            rows={2}
            className="text-sm"
            autoFocus
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={saving}>
            {saving ? "Saving…" : "Confirm change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
